import axios from "axios";

/**
 * Resolve the backend base URL.
 *
 * In production, users sometimes share links with the `www.` prefix
 * (e.g. https://www.flintislamiccenter.com/summer) — but the build-time
 * REACT_APP_BACKEND_URL is set to the bare-apex origin
 * (https://flintislamiccenter.com). That cross-subdomain mismatch causes
 * the browser to block every XHR with a CORS error and the React app
 * renders the 404 fallback.
 *
 * Defence: when the page is being served by a browser AND the current
 * window origin shares the same root domain as REACT_APP_BACKEND_URL,
 * prefer window.location.origin. This makes API calls same-origin no
 * matter whether the visitor came in on `www.` or apex.
 */
function resolveBackend() {
  const configured = process.env.REACT_APP_BACKEND_URL || "";
  if (typeof window === "undefined" || !window.location) return configured;

  try {
    const here = window.location;
    if (!configured) return here.origin;
    const cfg = new URL(configured);
    // Strip leading "www." for comparison so apex/www are treated equal.
    const stripWww = (h) => h.replace(/^www\./i, "");
    if (stripWww(cfg.hostname) === stripWww(here.hostname)) {
      // Same root domain → use the origin the visitor is actually on.
      return here.origin;
    }
  } catch {
    // Malformed env URL — fall through to the configured value.
  }
  return configured;
}

const BACKEND = resolveBackend();
export const API_BASE = `${BACKEND}/api`;

export const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("fic_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/**
 * wpImg(url): wrap a flintislamiccenter.com/wp-content/* URL through our
 * lazy mirror endpoint so it's served from our own MongoDB after Bluehost
 * is gone. Pass-through for any URL that's already internal or non-WP.
 */
export function wpImg(url) {
  if (!url || typeof url !== "string") return url;
  if (url.startsWith("/api/files/")) return `${BACKEND}${url}`;
  if (url.includes("/api/files/")) return url;
  if (!/^https?:\/\/flintislamiccenter\.com\/wp-content\//i.test(url)) return url;
  return `${API_BASE}/wp-mirror?u=${encodeURIComponent(url)}`;
}
